import { HubViewportCard } from "@/components/layout/HubViewportCard";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { SearchableMultiSelect } from "@/components/ui/SearchableMultiSelect";
import { SortableTableHeader } from "@/components/ui/SortableTableHeader";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import { InventoryOverTimeChart } from "./InventoryOverTimeChart";
import {
  fetchInventoryHistory,
  type InventoryHistoryGranularity,
  type InventoryHistoryGroupBy,
  type InventoryHistoryMetric,
  type InventoryHistoryPoint,
  type InventoryHistorySeries,
} from "./inventoryOverTimeApi";

type SortDirection = "asc" | "desc";

function chicagoToday(): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const byType = new Map(parts.map((part) => [part.type, part.value]));
  return `${byType.get("year")}-${byType.get("month")}-${byType.get("day")}`;
}

function formatQuantity(value: number): string {
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 0,
  }).format(value);
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: Math.abs(value) >= 1_000 ? 0 : 2,
  }).format(value);
}

function formatPeriod(period: string): string {
  const date = new Date(`${period}T12:00:00`);
  if (Number.isNaN(date.getTime())) return period;
  return date.toLocaleDateString(undefined, {
    month: "short",
    year: "numeric",
  });
}

function pointForPeriod(
  series: InventoryHistorySeries,
  period: string | undefined,
): InventoryHistoryPoint | undefined {
  if (!period) return undefined;
  return series.points.find((point) => point.period === period);
}

export function InventoryOverTime() {
  const [metric, setMetric] = useState<InventoryHistoryMetric>("value");
  const [granularity, setGranularity] =
    useState<InventoryHistoryGranularity>("month");
  const [groupBy, setGroupBy] =
    useState<InventoryHistoryGroupBy>("category");
  const [from, setFrom] = useState("2025-12-01");
  const [to, setTo] = useState(chicagoToday);
  const [selectedLocations, setSelectedLocations] = useState<Set<string>>(
    new Set(),
  );
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(
    new Set(),
  );
  const [locationSearch, setLocationSearch] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [sortKey, setSortKey] = useState("latest");
  const [sortDirection, setSortDirection] =
    useState<SortDirection>("desc");

  const locationIds = useMemo(
    () => [...selectedLocations].map(Number),
    [selectedLocations],
  );
  const categoryIds = useMemo(
    () => [...selectedCategories].map(Number),
    [selectedCategories],
  );
  const historyQuery = useQuery({
    queryKey: [
      "accounting",
      "inventory-history",
      from,
      to,
      granularity,
      groupBy,
      [...locationIds].sort((left, right) => left - right),
      [...categoryIds].sort((left, right) => left - right),
    ],
    queryFn: () =>
      fetchInventoryHistory({
        from,
        to,
        granularity,
        groupBy,
        locationIds,
        categoryIds,
      }),
    staleTime: 5 * 60_000,
    enabled: Boolean(from && to && from <= to),
  });

  const report = historyQuery.data;
  const periods = report?.periods ?? [];
  const firstPeriod = periods[0];
  const latestPeriod = periods.at(-1);
  const showPeriodColumns = periods.length <= 24;
  const formatValue = metric === "value" ? formatMoney : formatQuantity;
  const sortedSeries = useMemo(() => {
    const rows = [...(report?.series ?? [])];
    const direction = sortDirection === "asc" ? 1 : -1;
    rows.sort((left, right) => {
      if (sortKey === "group") {
        return left.label.localeCompare(right.label) * direction;
      }
      const leftFirst = pointForPeriod(left, firstPeriod)?.[metric] ?? 0;
      const rightFirst = pointForPeriod(right, firstPeriod)?.[metric] ?? 0;
      const leftLatest = pointForPeriod(left, latestPeriod)?.[metric] ?? 0;
      const rightLatest = pointForPeriod(right, latestPeriod)?.[metric] ?? 0;
      if (sortKey === "change") {
        return (leftLatest - leftFirst - (rightLatest - rightFirst)) * direction;
      }
      if (sortKey.startsWith("period:")) {
        const period = sortKey.slice("period:".length);
        return (
          ((pointForPeriod(left, period)?.[metric] ?? 0) -
            (pointForPeriod(right, period)?.[metric] ?? 0)) *
          direction
        );
      }
      return (leftLatest - rightLatest) * direction;
    });
    return rows;
  }, [
    firstPeriod,
    latestPeriod,
    metric,
    report?.series,
    sortDirection,
    sortKey,
  ]);

  function onSort(nextKey: string): void {
    if (nextKey === sortKey) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(nextKey);
    setSortDirection(nextKey === "group" ? "asc" : "desc");
  }

  const locationOptions = (report?.locations ?? []).map((location) => ({
    value: String(location.id),
    label: location.name,
  }));
  const categoryOptions = (report?.categories ?? []).map((category) => ({
    value: String(category.id),
    label: category.name,
  }));

  return (
    <div className="space-y-5">
      <PageTitleWithHelp hoverHint="Physical inventory quantity and frozen landed-cost value over time. Current days come from the Megaventory inventory snapshot. Dates before live history capture are estimates reconstructed from goods in, goods out, adjustments, transfers, and work order movements.">
        Inventory over time
      </PageTitleWithHelp>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="space-y-1.5">
            <Label>Metric</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant={metric === "quantity" ? "primary" : "secondary"}
                onClick={() => setMetric("quantity")}
              >
                Quantity
              </Button>
              <Button
                type="button"
                size="sm"
                variant={metric === "value" ? "primary" : "secondary"}
                onClick={() => setMetric("value")}
              >
                Value
              </Button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="inventory-history-granularity">Time interval</Label>
            <select
              id="inventory-history-granularity"
              className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200"
              value={granularity}
              onChange={(event) =>
                setGranularity(
                  event.target.value as InventoryHistoryGranularity,
                )
              }
            >
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="inventory-history-group">Group by</Label>
            <select
              id="inventory-history-group"
              className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200"
              value={groupBy}
              onChange={(event) =>
                setGroupBy(event.target.value as InventoryHistoryGroupBy)
              }
            >
              <option value="sku">SKU</option>
              <option value="description">Description</option>
              <option value="category">Category</option>
              <option value="location">Location</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="inventory-history-from">From</Label>
              <Input
                id="inventory-history-from"
                type="date"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inventory-history-to">To</Label>
              <Input
                id="inventory-history-to"
                type="date"
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5 md:col-span-1 xl:col-span-2">
            <Label htmlFor="inventory-history-locations">Locations</Label>
            <SearchableMultiSelect
              id="inventory-history-locations"
              variant="compact"
              selectedValues={selectedLocations}
              onSelectedValuesChange={setSelectedLocations}
              options={locationOptions}
              searchValue={locationSearch}
              onSearchChange={setLocationSearch}
              placeholder="All locations"
              emptySelectionMeansAll
              searchPlaceholder="Search locations..."
            />
          </div>
          <div className="space-y-1.5 md:col-span-1 xl:col-span-2">
            <Label htmlFor="inventory-history-categories">Categories</Label>
            <SearchableMultiSelect
              id="inventory-history-categories"
              variant="compact"
              selectedValues={selectedCategories}
              onSelectedValuesChange={setSelectedCategories}
              options={categoryOptions}
              searchValue={categorySearch}
              onSearchChange={setCategorySearch}
              placeholder="All categories"
              emptySelectionMeansAll
              searchPlaceholder="Search categories..."
            />
          </div>
        </CardBody>
      </Card>

      {from > to ? (
        <Card>
          <CardBody className="py-8 text-center text-sm text-rose-700">
            The From date must be on or before the To date.
          </CardBody>
        </Card>
      ) : historyQuery.isLoading ? (
        <Card>
          <CardBody className="flex items-center justify-center gap-2 py-12 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Loading inventory history...
          </CardBody>
        </Card>
      ) : historyQuery.error ? (
        <Card>
          <CardBody className="py-8 text-center text-sm text-rose-700">
            {historyQuery.error instanceof Error
              ? historyQuery.error.message
              : "Could not load inventory history."}
          </CardBody>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle>
                {metric === "value" ? "Inventory value" : "Inventory quantity"}
              </CardTitle>
              {report?.backfillThrough ? (
                <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                  Estimated through {formatPeriod(report.backfillThrough)}
                </span>
              ) : null}
            </CardHeader>
            <CardBody>
              <InventoryOverTimeChart
                periods={periods}
                series={report?.series ?? []}
                metric={metric}
                formatValue={formatValue}
              />
            </CardBody>
          </Card>

          <HubViewportCard>
            <CardHeader>
              <CardTitle>
                {String(sortedSeries.length)} grouped series
              </CardTitle>
            </CardHeader>
            <CardBody className="min-h-0 overflow-auto p-0">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 backdrop-blur-sm">
                  <tr>
                    <SortableTableHeader
                      label="Group"
                      sortKey="group"
                      activeKey={sortKey}
                      dir={sortDirection}
                      onSort={onSort}
                      className="min-w-48"
                    />
                    {showPeriodColumns ? (
                      periods.map((period) => (
                        <SortableTableHeader
                          key={period}
                          label={formatPeriod(period)}
                          sortKey={`period:${period}`}
                          activeKey={sortKey}
                          dir={sortDirection}
                          onSort={onSort}
                          align="right"
                          className="whitespace-nowrap"
                        />
                      ))
                    ) : (
                      <>
                        <SortableTableHeader
                          label={firstPeriod ? formatPeriod(firstPeriod) : "First"}
                          sortKey={`period:${firstPeriod ?? ""}`}
                          activeKey={sortKey}
                          dir={sortDirection}
                          onSort={onSort}
                          align="right"
                        />
                        <SortableTableHeader
                          label={latestPeriod ? formatPeriod(latestPeriod) : "Latest"}
                          sortKey="latest"
                          activeKey={sortKey}
                          dir={sortDirection}
                          onSort={onSort}
                          align="right"
                        />
                        <SortableTableHeader
                          label="Change"
                          sortKey="change"
                          activeKey={sortKey}
                          dir={sortDirection}
                          onSort={onSort}
                          align="right"
                        />
                        <th className="bg-slate-50 px-2 py-2 text-right text-xs font-medium uppercase tracking-wide text-slate-600">
                          Change percent
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sortedSeries.length === 0 ? (
                    <tr>
                      <td
                        colSpan={showPeriodColumns ? periods.length + 1 : 5}
                        className="px-3 py-10 text-center text-slate-500"
                      >
                        No rows match these filters.
                      </td>
                    </tr>
                  ) : (
                    sortedSeries.map((entry) => {
                      const firstValue =
                        pointForPeriod(entry, firstPeriod)?.[metric] ?? 0;
                      const latestValue =
                        pointForPeriod(entry, latestPeriod)?.[metric] ?? 0;
                      const change = latestValue - firstValue;
                      const changePercent =
                        firstValue === 0 ? null : (change / firstValue) * 100;
                      return (
                        <tr
                          key={entry.key}
                          className="hover:bg-slate-50/80"
                        >
                          <td className="px-2 py-2 font-medium text-slate-900">
                            {entry.label}
                          </td>
                          {showPeriodColumns ? (
                            periods.map((period) => {
                              const point = pointForPeriod(entry, period);
                              return (
                                <td
                                  key={period}
                                  className={cn(
                                    "whitespace-nowrap px-2 py-2 text-right tabular-nums",
                                    point?.estimated
                                      ? "bg-amber-50/50"
                                      : undefined,
                                  )}
                                >
                                  {formatValue(point?.[metric] ?? 0)}
                                </td>
                              );
                            })
                          ) : (
                            <>
                              <td className="px-2 py-2 text-right tabular-nums">
                                {formatValue(firstValue)}
                              </td>
                              <td className="px-2 py-2 text-right tabular-nums">
                                {formatValue(latestValue)}
                              </td>
                              <td
                                className={cn(
                                  "px-2 py-2 text-right tabular-nums",
                                  change > 0
                                    ? "text-emerald-700"
                                    : change < 0
                                      ? "text-rose-700"
                                      : "text-slate-600",
                                )}
                              >
                                {formatValue(change)}
                              </td>
                              <td className="px-2 py-2 text-right tabular-nums">
                                {changePercent == null
                                  ? "Not available"
                                  : `${changePercent.toFixed(1)}%`}
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </CardBody>
          </HubViewportCard>
        </>
      )}
    </div>
  );
}
